/**
 * The tour's React context, in its own module so `TourWelcome` (which the
 * provider renders) can read it without importing the provider back.
 */
import { createContext, useContext } from "react";
import type { TourStep } from "../../lib/tour/steps";

export interface TourContextValue {
  /** Start from the first step — the Guide row and the welcome card. */
  start: () => void;
  /** Jump straight to a step by index (the Guide's per-section "Show me"),
   *  activating the tour there and navigating to that step's route. */
  goToStep: (index: number) => void;
  /** Skip tour / Esc: marks skipped; the Guide restarts it. */
  skip: () => void;
  isActive: boolean;
  /** The step on screen, or null when the tour is not running. */
  step: TourStep | null;
  /** The app has at least one rendered video to open — gates steps whose
   *  anchor only exists once a video does (`TourStep.requiresVideo`). */
  hasVideo: boolean;
}

/** Outside a provider every call is a no-op — a screen rendered on its own
 *  (a test, a preview) must never throw for want of a tour. */
export const NOOP_TOUR: TourContextValue = { start: () => {}, goToStep: () => {}, skip: () => {}, isActive: false, step: null, hasVideo: false };

export const TourContext = createContext<TourContextValue>(NOOP_TOUR);

export function useTour(): TourContextValue {
  return useContext(TourContext);
}
